// apps/landing-page/src/components/payment/PaymentInstructions.tsx
'use client';

import { useState } from 'react';
import Image from 'next/image';
import { Copy, Check, Building2, QrCode, Wallet } from 'lucide-react';
import QRCode from 'react-qr-code';
import type { PaymentInstructions as Instructions } from '@/lib/api/payments';
import { isKnownImageHost } from '@/lib/image-hosts';
import { useTranslations } from 'next-intl';

/** Minimal shape PaymentInstructions needs to render the destination block's logo/name —
 *  deliberately narrower than the full OrderPaymentMethod (id/code aren't used here). */
interface DestinationMethod {
  name: string;
  iconUrl: string | null;
}

export function PaymentInstructions({
  instructions,
  paymentMethod,
  processor,
}: {
  instructions: Instructions;
  paymentMethod?: DestinationMethod | null;
  /**
   * Payment processor behind this VA, i.e. the owning order's `gateway`. Shown
   * in the destination block because a real bank receipt names the processor,
   * not just the merchant.
   *
   * Threaded in rather than hardcoded: this used to render a literal "DOKU" on
   * the assumption that DOKU was the only processor that could ever issue a VA
   * here. Once that stopped being true (Midtrans), every Midtrans VA still
   * displayed "DOKU" — a competitor's brand on the payer's confirmation screen.
   * Omitted entirely when unknown; a missing line is strictly better than a
   * wrong one. Mirrors dashboard's copy of this component, which already got
   * this right.
   */
  processor?: string | null;
}) {
  if (instructions.type === 'VA') {
    return (
      <VAInstructions
        bank={instructions.bank}
        vaNumber={instructions.vaNumber}
        receiverName={instructions.receiverName}
        paymentMethod={paymentMethod}
        processor={processor}
      />
    );
  }

  if (instructions.type === 'QRIS') {
    return <QRISInstructions qrString={instructions.qrString} qrImageUrl={instructions.qrImageUrl} />;
  }

  return (
    <EWalletInstructions
      provider={instructions.provider}
      deeplinkUrl={instructions.deeplinkUrl}
      qrImageUrl={instructions.qrImageUrl}
    />
  );
}

// --- VA ---

function VAInstructions({
  bank,
  vaNumber,
  receiverName,
  paymentMethod,
  processor,
}: {
  bank: string;
  vaNumber: string;
  receiverName?: string;
  paymentMethod?: DestinationMethod | null;
  processor?: string | null;
}) {
  const t = useTranslations('paymentInstructions');
  // DOKU space-pads virtualAccountNo to the BI-SNAP width. The padding is
  // load-bearing on the persisted id (the status call slices a partnerServiceId
  // out of it) but must never reach the payer: some banking apps reject a
  // pasted value with leading spaces. New orders arrive trimmed from the
  // gateway; this also covers rows created before that fix.
  const displayVa = vaNumber.trim();
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(displayVa).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <h3 className="text-sm font-bold text-slate-900">
          {t('vaTitle', { bank: bank.toUpperCase() })}
        </h3>
      </div>

      {/* Destination block — lets a nervous first-time payer match this against their
          banking app's confirmation screen (receiver name + "DOKU" as the processor)
          before they commit to the transfer. Logo box sizing (w-12 h-7, object-contain)
          matches the payment-method picker row in CartDrawer.tsx (PR #465). */}
      <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5 space-y-2">
        <p className="text-xs font-semibold text-slate-700">{t('tujuanTitle')}</p>
        <div className="flex items-start gap-2">
          <div className="w-12 h-7 flex items-center justify-center flex-shrink-0">
            {paymentMethod?.iconUrl ? (
              <Image
                src={paymentMethod.iconUrl}
                alt={paymentMethod.name}
                width={48}
                height={28}
                unoptimized={!isKnownImageHost(paymentMethod.iconUrl)}
                className="w-full h-full object-contain"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <Building2 className="w-5 h-5 text-slate-300" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">
              {paymentMethod?.name ?? bank.toUpperCase()}
            </p>
            {receiverName && (
              <p className="text-xs text-slate-600 truncate mt-0.5">{receiverName}</p>
            )}
            {processor && <p className="text-xs text-slate-400 mt-0.5">{processor}</p>}
          </div>
        </div>
      </div>

      <div>
        <p className="text-xs text-slate-500 mb-1">{t('vaNumber')}</p>
        <div className="flex items-center gap-2">
          <span className="font-mono text-base font-bold text-slate-900 tracking-widest select-all">
            {displayVa}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold hover:bg-teal-100 transition-colors cursor-pointer"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5" />
                {t('copied')}
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                {t('copy')}
              </>
            )}
          </button>
        </div>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5 space-y-1">
        <p className="text-xs font-semibold text-slate-700">{t('howToPay')}</p>
        <ol className="list-decimal list-inside space-y-0.5 text-xs text-slate-600">
          <li>{t('vaStep1')}</li>
          <li>{t('vaStep2')}</li>
          <li>{t('vaStep3')}</li>
          <li>{t('vaStep4')}</li>
        </ol>
      </div>
    </div>
  );
}

// --- QRIS ---

function QRISInstructions({
  qrString,
  qrImageUrl,
}: {
  qrString: string;
  qrImageUrl?: string;
}) {
  const t = useTranslations('paymentInstructions');

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <QrCode className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <h3 className="text-sm font-bold text-slate-900">{t('qrisTitle')}</h3>
      </div>

      <div className="flex flex-col items-center gap-3">
        {qrImageUrl ? (
          // Gateway-generated QR image (host varies by active payment gateway —
          // Xendit/Midtrans, admin-switchable — see PAYMENT_GATEWAY_CREDS memory).
          // Not coverable by a fixed remotePattern without breaking on a gateway
          // switch, and a broken checkout QR is worse than an unoptimized one.
          <Image
            src={qrImageUrl}
            alt={t('qrisImageAlt')}
            width={210}
            height={210}
            unoptimized
            className="rounded-lg border border-slate-100"
          />
        ) : (
          <div className="rounded-lg bg-white border border-slate-200 p-3 flex items-center justify-center">
            <QRCode
              value={qrString}
              size={210}
              bgColor="#ffffff"
              fgColor="#0f172a"
              level="M"
              style={{ height: 'auto', maxWidth: '100%', width: '210px' }}
            />
          </div>
        )}
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5 space-y-1">
        <p className="text-xs font-semibold text-slate-700">{t('howToPay')}</p>
        <ol className="list-decimal list-inside space-y-0.5 text-xs text-slate-600">
          <li>{t('qrisStep1')}</li>
          <li>{t('qrisStep2')}</li>
          <li>{t('qrisStep3')}</li>
          <li>{t('qrisStep4')}</li>
        </ol>
      </div>
    </div>
  );
}

// --- E-Wallet ---

function EWalletInstructions({
  provider,
  deeplinkUrl,
  qrImageUrl,
}: {
  provider: string;
  deeplinkUrl?: string;
  qrImageUrl?: string;
}) {
  const t = useTranslations('paymentInstructions');
  const providerLabel = provider.toUpperCase();

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Wallet className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <h3 className="text-sm font-bold text-slate-900">
          {t('ewalletTitle', { provider: providerLabel })}
        </h3>
      </div>

      {deeplinkUrl && (
        <a
          href={deeplinkUrl}
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-bold text-sm bg-teal-600 text-white hover:bg-teal-700 transition-colors cursor-pointer"
        >
          {t('openApp', { provider: providerLabel })}
        </a>
      )}

      {qrImageUrl && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-xs text-slate-500">
            {t('orScanQr', { provider: providerLabel })}
          </p>
          {/* Same gateway-host caveat as the QRIS image above. */}
          <Image
            src={qrImageUrl}
            alt={t('qrImageAlt', { provider: providerLabel })}
            width={200}
            height={200}
            unoptimized
            className="rounded-lg border border-slate-100"
          />
        </div>
      )}

      {!deeplinkUrl && !qrImageUrl && (
        <p className="text-xs text-slate-500">
          {t('followInstructions', { provider: providerLabel })}
        </p>
      )}
    </div>
  );
}
